/**
 * Case Study Filter Module
 * Handles filtering, search, and load more functionality for case studies archive
 * 
 * @package RadianHub
 */

export function initCaseStudyFilter() {
    const filterContainer = document.querySelector('.case-study-filters');
    if (!filterContainer) return;
    
    const grid = document.getElementById('case-studies-grid');
    const loadMoreBtn = document.getElementById('load-more-case-studies');
    const searchInput = document.getElementById('case-study-search');
    const activeFiltersContainer = document.getElementById('active-filters');
    const activeFiltersList = document.getElementById('active-filters-list');
    
    if (!grid) return;

    /** Full grid replace (filters / search / popstate): show spinner until AJAX completes */
    const gridLoadingHtml = `
        <div class="case-studies-grid-loading" role="status" aria-live="polite">
            <span class="case-studies-grid-loading-spinner" aria-hidden="true"></span>
            <span class="font-alegreya text-radianhub-teal text-sm">Loading case studies…</span>
        </div>`;
    
    let currentPage = 1;
    let isLoading = false;
    let activeFilters = {
        cloud: [],
        service: [],
        industry: [],
        search: ''
    };
    
    // Debounce function for search
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
    
    // Uncheck all tag checkboxes except the given one (single-tag selection)
    function enforceSingleTag(exceptCheckbox = null) {
        filterContainer.querySelectorAll('.case-study-filter-checkbox').forEach(cb => {
            if (cb !== exceptCheckbox) cb.checked = false;
        });
    }
    
    // Get all active filter checkboxes (at most one tag across cloud/service/industry)
    function getActiveFilters() {
        const filters = {
            cloud: [],
            service: [],
            industry: [],
            search: searchInput ? searchInput.value.trim() : ''
        };
        
        // Get checked checkboxes (single-tag enforced, so at most one)
        const checkboxes = filterContainer.querySelectorAll('.case-study-filter-checkbox:checked');
        checkboxes.forEach(checkbox => {
            const label = checkbox.closest('.case-study-filter-label');
            const taxonomy = checkbox.getAttribute('data-taxonomy');
            const termId = parseInt(checkbox.getAttribute('data-term-id'));
            const termName = (label.querySelector('.case-study-filter-link') || label.querySelector('span'))?.textContent?.trim() || '';
            const termSlug = label?.getAttribute('data-term-slug') || '';
            const item = { id: termId, name: termName, slug: termSlug };
            if (taxonomy === 'case_study_cloud') {
                filters.cloud.push(item);
            } else if (taxonomy === 'case_study_service') {
                filters.service.push(item);
            } else if (taxonomy === 'case_study_industry') {
                filters.industry.push(item);
            }
        });
        return filters;
    }
    
    // Update active filters display
    function updateActiveFiltersDisplay() {
        if (!activeFiltersList || !activeFiltersContainer) return;
        
        activeFiltersList.innerHTML = '';
        
        const allFilters = [
            ...activeFilters.cloud.map(f => ({ ...f, taxonomy: 'cloud' })),
            ...activeFilters.service.map(f => ({ ...f, taxonomy: 'service' })),
            ...activeFilters.industry.map(f => ({ ...f, taxonomy: 'industry' }))
        ];
        
        if (allFilters.length === 0 && !activeFilters.search) {
            activeFiltersContainer.classList.add('hidden');
            return;
        }
        
        activeFiltersContainer.classList.remove('hidden');
        
        // Add search filter if present
        if (activeFilters.search) {
            const searchFilter = document.createElement('div');
            searchFilter.className = 'inline-flex items-center gap-2 px-3 py-1 bg-[#104F55] text-white rounded-full text-sm';
            searchFilter.innerHTML = `
                <span style="font-family: 'Alegreya', sans-serif;">Search: "${activeFilters.search}"</span>
                <button type="button" class="remove-filter" data-filter-type="search" aria-label="Remove search filter">
                    <span class="remove-filter-icon text-white hover:text-gray-200">×</span>
                </button>
            `;
            activeFiltersList.appendChild(searchFilter);
        }
        
        // Add taxonomy filters
        allFilters.forEach(filter => {
            const filterEl = document.createElement('div');
            filterEl.className = 'inline-flex items-center gap-2 px-3 py-1 bg-[#104F55] text-white rounded-full text-sm';
            filterEl.innerHTML = `
                <span style="font-family: 'Alegreya', sans-serif;">${filter.name}</span>
                <button type="button" class="remove-filter" data-filter-type="${filter.taxonomy}" data-term-id="${filter.id}" aria-label="Remove ${filter.name} filter">
                    <span class="remove-filter-icon text-white hover:text-gray-200">×</span>
                </button>
            `;
            activeFiltersList.appendChild(filterEl);
        });
    }
    
    // Load case studies via AJAX
    function loadCaseStudies(page = 1, append = false) {
        if (isLoading) return;
        
        isLoading = true;
        currentPage = page;

        if (!append) {
            grid.setAttribute('aria-busy', 'true');
            grid.innerHTML = gridLoadingHtml;
        }
        
        // Update button state
        if (loadMoreBtn) {
            loadMoreBtn.disabled = true;
            loadMoreBtn.textContent = 'Loading...';
        }
        
        // Prepare data
        const data = {
            action: 'radianhub_filter_case_studies',
            nonce: window.radianhub_ajax?.nonce || '',
            page: page,
            cloud_terms: activeFilters.cloud.map(f => f.id),
            service_terms: activeFilters.service.map(f => f.id),
            industry_terms: activeFilters.industry.map(f => f.id),
            search: activeFilters.search
        };
        
        // Make AJAX request
        fetch(window.radianhub_ajax?.ajax_url || '/wp-admin/admin-ajax.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(data)
        })
        .then(response => {
            // Check if response is actually JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                return response.text().then(text => {
                    // Non-JSON response from server
                    throw new Error('Server returned non-JSON response. Check console for details.');
                });
            }
            return response.json();
        })
        .then(result => {
            // Handle both wp_send_json and wp_send_json_success formats
            const data = result.data || result;
            const success = result.success !== undefined ? result.success : (data && data.success !== undefined ? data.success : true);
            
            if (success && data) {
                if (append) {
                    grid.insertAdjacentHTML('beforeend', data.posts || '');
                } else {
                    grid.innerHTML = data.posts || '';
                    // Don't scroll - keep user's current scroll position
                }
                
                // Update load more button
                if (loadMoreBtn) {
                    if (data.has_more) {
                        loadMoreBtn.disabled = false;
                        loadMoreBtn.textContent = 'Load More';
                        loadMoreBtn.setAttribute('data-page', data.current_page || page);
                        loadMoreBtn.setAttribute('data-total', data.found_posts || 0);
                        loadMoreBtn.closest('div').style.display = 'block';
                    } else {
                        loadMoreBtn.closest('div').style.display = 'none';
                    }
                }
            } else {
                // Error loading case studies
                if (!append) {
                    grid.innerHTML = '<p class="col-span-full">No case studies found.</p>';
                }
            }
        })
        .catch(error => {
            // AJAX error occurred
            if (!append) {
                grid.innerHTML = '<p class="col-span-full">Error loading case studies. Please try again.</p>';
            }
        })
        .finally(() => {
            isLoading = false;
            if (!append) {
                grid.removeAttribute('aria-busy');
            }
            if (loadMoreBtn) {
                loadMoreBtn.disabled = false;
                loadMoreBtn.textContent = 'Load More';
            }
        });
    }
    
    // Build current filter URL from active filters (slug-based params) and update browser URL
    function getFilterUrl() {
        const base = (typeof window.radianhub_case_study_archive !== 'undefined' && window.radianhub_case_study_archive.archive_url)
            ? window.radianhub_case_study_archive.archive_url
            : (window.location.pathname + window.location.search);
        const params = new URLSearchParams();
        activeFilters.cloud.forEach(f => { if (f.slug) params.append('cloud', f.slug); });
        activeFilters.service.forEach(f => { if (f.slug) params.append('service', f.slug); });
        activeFilters.industry.forEach(f => { if (f.slug) params.append('industry', f.slug); });
        const query = params.toString();
        return query ? `${base.replace(/\?.*$/, '')}?${query}` : base.replace(/\?.*$/, '');
    }
    function updateFilterUrl() {
        if (typeof window.radianhub_case_study_archive === 'undefined') return;
        const url = getFilterUrl();
        if (url !== window.location.href) {
            window.history.replaceState({ caseStudyFilter: true }, '', url);
        }
    }

    // Handle filter checkbox changes
    function handleFilterChange() {
        activeFilters = getActiveFilters();
        updateActiveFiltersDisplay();
        updateFilterUrl();
        loadCaseStudies(1, false); // Reset to page 1
    }
    
    // Handle search input
    const handleSearch = debounce(() => {
        activeFilters = getActiveFilters();
        updateActiveFiltersDisplay();
        loadCaseStudies(1, false); // Reset to page 1
    }, 300);
    
    // Handle remove filter
    function handleRemoveFilter(e) {
        const button = e.target.closest('.remove-filter');
        if (!button) return;
        
        const filterType = button.getAttribute('data-filter-type');
        const termId = button.getAttribute('data-term-id');
        
        if (filterType === 'search') {
            if (searchInput) {
                searchInput.value = '';
            }
            activeFilters.search = '';
        } else {
            // Find and uncheck the corresponding checkbox
            const checkbox = filterContainer.querySelector(
                `.case-study-filter-checkbox[data-taxonomy="case_study_${filterType}"][data-term-id="${termId}"]`
            );
            if (checkbox) {
                checkbox.checked = false;
            }
        }
        
        activeFilters = getActiveFilters();
        updateActiveFiltersDisplay();
        updateFilterUrl();
        loadCaseStudies(1, false); // Reset to page 1
    }

    // Apply initial filter state from URL (window.radianhub_case_study_archive)
    // Single-tag: only apply the first filter found (cloud > service > industry)
    function applyInitialFiltersFromUrl() {
        const archive = window.radianhub_case_study_archive;
        if (!archive || !filterContainer) return;
        const cloud = (archive.cloud || [])[0];
        const service = (archive.service || [])[0];
        const industry = (archive.industry || [])[0];
        const first = cloud ? { taxonomy: 'case_study_cloud', id: cloud }
            : service ? { taxonomy: 'case_study_service', id: service }
            : industry ? { taxonomy: 'case_study_industry', id: industry }
            : null;
        if (first) {
            const cb = filterContainer.querySelector(`.case-study-filter-checkbox[data-taxonomy="${first.taxonomy}"][data-term-id="${first.id}"]`);
            if (cb) {
                cb.checked = true;
                enforceSingleTag(cb);
            }
        }
        activeFilters = getActiveFilters();
        updateActiveFiltersDisplay();
        updateFilterUrl();
        if (first && !archive.server_rendered_filter) {
            loadCaseStudies(1, false);
        }
    }
    
    // Event listeners
    if (filterContainer) {
        // Checkbox changes - enforce single-tag: when checked, uncheck all others
        filterContainer.addEventListener('change', (e) => {
            if (e.target.classList.contains('case-study-filter-checkbox')) {
                if (e.target.checked) enforceSingleTag(e.target);
                handleFilterChange();
            }
        });
        
        // Label clicks - toggle checkbox and filter; allow link to open in new tab (ctrl/cmd+click)
        filterContainer.addEventListener('click', (e) => {
            const label = e.target.closest('.case-study-filter-label');
            if (!label) return;
            if (e.target.classList.contains('case-study-filter-checkbox')) return;
            // If click is on the link and user wants new tab, don't intercept
            if (e.target.closest('a.case-study-filter-link') && (e.ctrlKey || e.metaKey)) return;

            e.preventDefault();
            e.stopPropagation();
            const checkbox = label.querySelector('.case-study-filter-checkbox');
            if (checkbox) {
                if (!checkbox.checked) enforceSingleTag(checkbox); // Before checking, uncheck others
                checkbox.checked = !checkbox.checked;
                handleFilterChange();
            }
        });
    }
    
    // Search input
    if (searchInput) {
        searchInput.addEventListener('input', handleSearch);
    }
    
    // Remove filter buttons
    if (activeFiltersList) {
        activeFiltersList.addEventListener('click', handleRemoveFilter);
    }
    
    // Load more button
    if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', () => {
            const nextPage = currentPage + 1;
            loadCaseStudies(nextPage, true); // Append to existing
        });
    }
    
    // Category accordion toggles (Filter by Cloud, Service, Industry)
    const isMobile = () => window.matchMedia('(max-width: 639px)').matches;
    filterContainer.querySelectorAll('.filter-category-toggle').forEach(btn => {
        const contentId = btn.getAttribute('aria-controls');
        const content = contentId ? document.getElementById(contentId) : null;
        if (!content) return;
        // On mobile, start collapsed; on desktop, start expanded
        if (isMobile()) {
            btn.setAttribute('aria-expanded', 'false');
            content.classList.add('hidden');
            // Chevron stays down when collapsed (no rotate-180)
        }
        btn.addEventListener('click', () => {
            const expanded = btn.getAttribute('aria-expanded') === 'true';
            const willBeExpanded = !expanded;
            btn.setAttribute('aria-expanded', willBeExpanded);
            content.classList.toggle('hidden', expanded);
            // Caret down when collapsed, up when expanded. rotate-180 = up.
            btn.querySelector('.filter-category-icon')?.classList.toggle('rotate-180', willBeExpanded);
        });
    });
    
    // Initialize: apply URL params if present and run filter; otherwise sync display
    applyInitialFiltersFromUrl();

    // Keep URL in sync when user uses browser back/forward (single-tag: first param wins)
    window.addEventListener('popstate', () => {
        const archive = window.radianhub_case_study_archive;
        if (!archive || !filterContainer) return;
        const params = new URLSearchParams(window.location.search);
        const types = ['cloud', 'service', 'industry'];
        const taxonomyMap = { cloud: 'case_study_cloud', service: 'case_study_service', industry: 'case_study_industry' };
        let found = false;
        for (const type of types) {
            const slugs = params.getAll(type);
            if (slugs.length > 0) {
                const taxonomy = taxonomyMap[type];
                const slug = slugs[0]; // Single-tag: only first slug
                filterContainer.querySelectorAll(`.case-study-filter-checkbox[data-taxonomy="${taxonomy}"]`).forEach(cb => {
                    const cbSlug = cb.closest('.case-study-filter-label')?.getAttribute('data-term-slug');
                    cb.checked = cbSlug === slug;
                    if (cb.checked) enforceSingleTag(cb);
                });
                found = true;
                break;
            }
        }
        if (!found) enforceSingleTag();
        activeFilters = getActiveFilters();
        updateActiveFiltersDisplay();
        loadCaseStudies(1, false);
    });
}
